/*
 * Luxe & Gloss privacy-safe storefront interaction bus, contract version 1.
 *
 * This file does not write cookies/storage, make network requests, push to
 * dataLayer/gtag, or emit purchase. Shopify Customer Events and installed app
 * pixels remain the authority for commerce and consent. Approved adapters may
 * subscribe to `lg:analytics:event` after J verifies consent and deduplication.
 */
(function (root, document) {
  'use strict'

  if (!root || !document) return

  root.LG = root.LG || {}
  if (root.LG.analytics && root.LG.analytics.version === 1) return

  const VERSION = 1
  const MAX_QUEUE_SIZE = 50
  const EVENT_NAMES = new Set([
    'view_item',
    'select_item',
    'fit_completed',
    'add_to_cart',
    'view_cart',
    'begin_checkout',
    'custom_request_started',
    'custom_request_submitted'
  ])
  const subscribers = new Set()
  const dedupe = new Map()
  const queue = []
  let eventSequence = 0
  let context = readContext()
  let latestCart = context.cart || null
  let initialized = false

  function readContext () {
    const element = document.querySelector('[data-lg-analytics-context]')
    if (!element) return { version: VERSION, page: {}, shop: {} }

    try {
      const parsed = JSON.parse(element.textContent || '{}')
      if (!parsed || parsed.version !== VERSION) return { version: VERSION, page: {}, shop: {} }
      return parsed
    } catch (error) {
      return { version: VERSION, page: {}, shop: {} }
    }
  }

  function cleanText (value, maximumLength) {
    if (typeof value !== 'string' && typeof value !== 'number') return undefined
    const result = String(value).replace(/\s+/g, ' ').trim().slice(0, maximumLength || 160)
    return result || undefined
  }

  function cleanId (value) {
    const result = cleanText(value, 64)
    return result && /^[A-Za-z0-9:_-]+$/.test(result) ? result : undefined
  }

  function cleanEntityId (value) {
    const result = cleanText(value, 160)
    if (!result) return undefined
    if (/^[A-Za-z0-9:_-]+$/.test(result)) return result
    return /^gid:\/\/shopify\/[A-Za-z]+\/[0-9]+$/.test(result) ? result : undefined
  }

  function cleanVersion (value) {
    const result = cleanText(value, 80)
    return result && /^[A-Za-z0-9._:-]+$/.test(result) ? result : undefined
  }

  function cleanHandle (value) {
    const result = cleanText(value, 160)
    return result && /^[a-z0-9][a-z0-9_-]*$/i.test(result) ? result.toLowerCase() : undefined
  }

  function cleanInteger (value) {
    if (value === undefined || value === null || value === '') return undefined
    const number = Number(value)
    return Number.isInteger(number) && number >= 0 ? number : undefined
  }

  function cleanMoney (value) {
    if (value === undefined || value === null || value === '') return undefined
    const number = Number(value)
    return Number.isFinite(number) && number >= 0 ? Math.round(number * 100) / 100 : undefined
  }

  function put (target, key, value) {
    if (value !== undefined) target[key] = value
  }

  function cleanItem (item) {
    const result = {}
    put(result, 'item_id', cleanEntityId(item && item.item_id))
    put(result, 'item_handle', cleanHandle(item && item.item_handle))
    put(result, 'item_name', cleanText(item && item.item_name, 160))
    put(result, 'variant_id', cleanEntityId(item && item.variant_id))
    put(result, 'variant_name', cleanText(item && item.variant_name, 100))
    put(result, 'value', cleanMoney(item && item.value))
    put(result, 'currency', cleanId(item && item.currency))
    if (item && typeof item.available === 'boolean') result.available = item.available
    return result
  }

  function cleanCart (cart) {
    const result = {}
    const rawTotalPrice = cart && cart.total_price
    const totalPrice = rawTotalPrice === undefined || rawTotalPrice === null || rawTotalPrice === ''
      ? Number.NaN
      : Number(rawTotalPrice)
    put(result, 'item_count', cleanInteger(cart && cart.item_count))
    put(result, 'value', Number.isFinite(totalPrice) && totalPrice >= 0 ? cleanMoney(totalPrice / 100) : undefined)
    put(result, 'currency', cleanId((cart && (cart.currency && cart.currency.iso_code ? cart.currency.iso_code : cart.currency)) || context.shop.currency))
    return result
  }

  function cleanFit (detail) {
    const result = {}
    put(result, 'product_id', cleanEntityId(detail && detail.productId))
    put(result, 'fit_system_id', cleanEntityId(detail && detail.fitSystemId))
    put(result, 'fit_system_version', cleanVersion(detail && detail.fitSystemVersion))
    put(result, 'mode', cleanId(detail && detail.mode))
    put(result, 'recommended_variant_id', cleanEntityId(detail && detail.recommendedVariantId))
    return result
  }

  function cleanEventData (name, data) {
    const source = cleanId(data && data.source)
    const surface = cleanId(data && data.surface)
    const requestId = cleanId(data && data.request_id)

    switch (name) {
      case 'view_item':
        return { item: cleanItem(data && data.item) }
      case 'select_item':
        return {
          item: cleanItem(data && data.item),
          ...(source ? { source } : {})
        }
      case 'fit_completed':
        return cleanFit(data)
      case 'add_to_cart':
        return {
          cart: cleanCart(data && data.cart),
          ...(source ? { source } : {}),
          ...(requestId ? { request_id: requestId } : {})
        }
      case 'view_cart':
      case 'begin_checkout':
        return {
          cart: cleanCart(data && data.cart),
          ...(surface ? { surface } : {})
        }
      case 'custom_request_started':
      case 'custom_request_submitted':
        return surface ? { surface } : {}
      default:
        return {}
    }
  }

  function pageContext () {
    const result = {}
    put(result, 'type', cleanId(context.page && context.page.type))
    put(result, 'path', cleanText(root.location && root.location.pathname, 300))
    put(result, 'locale', cleanId(context.page && context.page.locale))
    return result
  }

  function eventId () {
    eventSequence += 1
    const random = root.crypto && typeof root.crypto.randomUUID === 'function'
      ? root.crypto.randomUUID()
      : Math.random().toString(36).slice(2)
    return `lg-${Date.now()}-${eventSequence}-${random}`
  }

  function isDuplicate (key, ttl) {
    if (!key) return false
    const now = Date.now()
    const previous = dedupe.get(key)
    if (previous !== undefined && now - previous < (ttl === undefined ? Number.POSITIVE_INFINITY : ttl)) return true
    dedupe.set(key, now)
    return false
  }

  function emit (name, data, options) {
    if (!EVENT_NAMES.has(name)) throw new TypeError(`Unsupported analytics event: ${name}`)
    if (isDuplicate(options && options.dedupeKey, options && options.dedupeTtl)) return null

    const payload = {
      version: VERSION,
      event: name,
      event_id: eventId(),
      occurred_at: new Date().toISOString(),
      page: pageContext(),
      data: cleanEventData(name, data || {})
    }

    queue.push(payload)
    if (queue.length > MAX_QUEUE_SIZE) queue.shift()

    root.dispatchEvent(new root.CustomEvent('lg:analytics:event', { detail: payload }))
    subscribers.forEach(listener => {
      try {
        listener(payload)
      } catch (error) {
        // A reporting adapter must never break the storefront interaction.
      }
    })
    return payload
  }

  function subscribe (listener) {
    if (typeof listener !== 'function') throw new TypeError('Analytics subscriber must be a function.')
    subscribers.add(listener)
    return () => subscribers.delete(listener)
  }

  function getQueue () {
    return queue.map(payload => JSON.parse(JSON.stringify(payload)))
  }

  function reset () {
    queue.length = 0
    dedupe.clear()
  }

  function productHandleFromLink (link) {
    try {
      const url = new URL(link.href, root.location.origin)
      const match = url.pathname.match(/\/products\/([^/]+)/)
      return match ? cleanHandle(match[1]) : undefined
    } catch (error) {
      return undefined
    }
  }

  function productLinkData (link) {
    const card = link.closest('.lg-card, .product-card')
    const title = card && card.querySelector('.lg-card__title, .product-card-title')
    return {
      item_handle: productHandleFromLink(link),
      item_name: title ? title.textContent : undefined
    }
  }

  function productLinkSource (link) {
    if (link.closest('.lg-search-page, [data-lg-search-results]')) return 'search'
    if (link.closest('[data-collection-root], .collection-products')) return 'collection'
    if (link.closest('.lg-home-product-grid, [data-lg-home-products]')) return 'home'
    if (link.closest('[data-lg-recommendations], .product-recommendations')) return 'recommendation'
    return 'catalog'
  }

  function customForm (element) {
    if (!context.custom_request || !element || typeof element.closest !== 'function') return null
    return element.closest('[data-lg-custom-request-form], #custom-order-form, form[action*="/contact"]')
  }

  function checkoutControl (element) {
    if (!element || typeof element.closest !== 'function') return null
    return element.closest('[name="checkout"], [data-lg-checkout], a[href*="/checkout"]')
  }

  function checkoutSurface (element) {
    if (element.closest('[data-lg-cart-drawer], #offcanvas-cart')) return 'drawer'
    if (element.closest('[data-lg-cart-page], .lg-cart-page')) return 'cart_page'
    return 'unknown'
  }

  function onDocumentClick (event) {
    const element = event.target && event.target.closest ? event.target : null
    if (!element) return

    const productLink = element.closest('a.lg-card__link, a.product-card-link')
    if (productLink) {
      const item = productLinkData(productLink)
      emit('select_item', { item, source: productLinkSource(productLink) }, {
        dedupeKey: `select:${item.item_handle || productLink.href}`,
        dedupeTtl: 750
      })
    }

    const checkout = checkoutControl(element)
    if (checkout) {
      const surface = checkoutSurface(checkout)
      emit('begin_checkout', { cart: latestCart, surface }, {
        dedupeKey: `checkout:${surface}`,
        dedupeTtl: 2000
      })
    }
  }

  function onDocumentSubmit (event) {
    const form = event.target
    if (customForm(form)) {
      emit('custom_request_started', { surface: 'custom_request_page' }, {
        dedupeKey: 'custom-request-started'
      })
    }

    const checkout = checkoutControl(event.submitter) || checkoutControl(form)
    if (checkout) {
      const surface = checkoutSurface(checkout)
      emit('begin_checkout', { cart: latestCart, surface }, {
        dedupeKey: `checkout:${surface}`,
        dedupeTtl: 2000
      })
    }
  }

  function onCustomInteraction (event) {
    if (!customForm(event.target)) return
    emit('custom_request_started', { surface: 'custom_request_page' }, {
      dedupeKey: 'custom-request-started'
    })
  }

  function onVariantChanged (event) {
    if (!context.item || !event.detail || event.detail.version !== VERSION) return
    if (cleanEntityId(event.detail.productId) !== cleanEntityId(context.item.item_id)) return
    context.item.variant_id = event.detail.variantId
    context.item.available = Boolean(event.detail.available)
  }

  function onFitConfirmed (event) {
    if (!event.detail || event.detail.version !== VERSION) return
    const detail = cleanFit(event.detail)
    const key = [
      detail.product_id,
      detail.fit_system_id,
      detail.fit_system_version,
      detail.mode,
      detail.recommended_variant_id
    ].join(':')
    emit('fit_completed', event.detail, {
      dedupeKey: `fit:${key}`,
      dedupeTtl: 1000
    })
  }

  function onCartUpdated (event) {
    const detail = event.detail
    if (!detail || detail.version !== VERSION || !detail.cart) return
    latestCart = detail.cart
    if (detail.action !== 'add') return

    emit('add_to_cart', {
      cart: detail.cart,
      source: detail.source,
      request_id: detail.requestId
    }, {
      dedupeKey: `cart-add:${cleanId(detail.requestId) || ''}`
    })
  }

  function onCartOpen () {
    emit('view_cart', { cart: latestCart, surface: 'drawer' }, {
      dedupeKey: 'view-cart:drawer',
      dedupeTtl: 1000
    })
  }

  function initialize () {
    if (initialized) return
    initialized = true

    document.addEventListener('click', onDocumentClick)
    document.addEventListener('submit', onDocumentSubmit)
    document.addEventListener('focusin', onCustomInteraction)
    document.addEventListener('input', onCustomInteraction)
    document.addEventListener('change', onCustomInteraction)
    root.addEventListener('lg:variant:changed', onVariantChanged)
    root.addEventListener('lg:fit:confirmed', onFitConfirmed)
    root.addEventListener('lg:cart:updated', onCartUpdated)
    root.addEventListener('lg:cart:open', onCartOpen)

    if (context.item) {
      emit('view_item', { item: context.item }, {
        dedupeKey: `view-item:${cleanEntityId(context.item.item_id) || cleanHandle(context.item.item_handle) || ''}`
      })
    }

    if (context.page && context.page.type === 'cart') {
      emit('view_cart', { cart: context.cart, surface: 'cart_page' }, {
        dedupeKey: 'view-cart:cart-page'
      })
    }

    if (context.custom_request && document.querySelector('[data-lg-custom-request-success], .custom-orders-success')) {
      emit('custom_request_submitted', { surface: 'custom_request_page' }, {
        dedupeKey: 'custom-request-submitted'
      })
    }
  }

  root.LG.analytics = Object.freeze({
    version: VERSION,
    emit,
    subscribe,
    getQueue,
    reset,
    initialize
  })

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true })
  else initialize()
})(window, document)
